import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import rg4js from "raygun4js";
import { useEffect, useMemo, useRef, useState } from "react";
import { Button, Spinner } from "react-bootstrap";
import { CheckCircleFill } from "react-bootstrap-icons";
import { useHistory, useLocation, useParams } from "react-router-dom";
import { CaseFileType } from "@/briefpoint-client";
import { toast } from "@/components/ui/toast";
import { useCaseFileApi } from "@/hooks/useApi";
import useBatchErrorToasts from "@/hooks/useBatchErrorToasts";
import { ACCEPTABLE_FILE_TYPES_WITH_IMAGES } from "@/lib/constants";
import { getPurgatorySummaryOptions } from "@/lib/query-options";
import { normalizeError, sanitizeFiles, uploadFilesToAzure } from "@/lib/utils";
import { pluralize } from "@/utils";
import styles from "./UploadMore.module.scss";

const UploadMore = () => {
  const urlParams = useParams<{ id: string }>();
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const uploadMoreInputRef = useRef<HTMLInputElement>(null);
  const history = useHistory();
  const caseFileApi = useCaseFileApi();
  const queryClient = useQueryClient();
  const [isPolling, setIsPolling] = useState(false);
  const [batchId, setBatchId] = useState<string | null>(null);
  const batchErrorToasts = useBatchErrorToasts();

  const purgatorySummaryQueryOptions = getPurgatorySummaryOptions(
    caseFileApi,
    urlParams.id,
  );
  const purgatorySummaryQuery = useQuery(purgatorySummaryQueryOptions);

  const uploadBatchesQueryKey = useMemo(
    () => [
      "caseFileApi.caseFileGetCaseFileUploadBatches",
      "UploadMore",
      { caseId: urlParams.id },
    ],
    [urlParams.id],
  );

  // When the azure upload is complete, poll the upload batch until it's empty
  const uploadBatchesQuery = useQuery({
    queryKey: uploadBatchesQueryKey,
    queryFn: () =>
      isPolling
        ? caseFileApi.caseFileGetCaseFileUploadBatches({ caseId: urlParams.id })
        : // Hack to stop multiple requests when the component is re-mounted.
          Promise.resolve([]),
    enabled: isPolling,
    retry: 1,
    retryDelay: 500,
    refetchInterval: (query) => {
      // When there are no documents in the batch, they have been uploaded. Stop polling.
      if (!query || query.state.data?.length === 0) {
        return false;
      }
      return 3000; // Poll every 3 seconds
    },
    refetchIntervalInBackground: true,
    staleTime: 0, // Always consider data stale to ensure fresh polling,
  });

  // The first step is to upload the files to Azure, then we poll the upload batch until it's empty.
  const uploadToAzureMutation = useMutation({
    mutationFn: async (files: File[]) => {
      const { data } = await uploadFilesToAzure({
        files,
        caseId: urlParams.id,
        type: CaseFileType.Produceable,
        caseFileApi,
      });
      return data;
    },
    onSuccess: async () => {
      if (uploadMoreInputRef.current) {
        uploadMoreInputRef.current.value = "";
      }

      setIsPolling(true);
    },
    onError: (error) => {
      toast.error(error.message);
      rg4js("send", {
        error: normalizeError(error),
      });
      toast.error("Unable to upload production documents.");
    },
    onSettled: () => {
      // Invalidate the case files query to refresh the data
      queryClient.invalidateQueries({
        queryKey: purgatorySummaryQueryOptions.queryKey,
      });
    },
  });

  // When all the files are uploaded and the batch is processed (i.e. empty)...
  useEffect(() => {
    if (!isPolling || !uploadBatchesQuery.data) return;

    // If the batch is not empty, continue polling
    if ((uploadBatchesQuery.data?.length || 0) > 0) {
      // Save the batch id to so we can get the final batch to report errors
      setBatchId(uploadBatchesQuery.data[0].id || null);
      // Continue polling until batch is empty
      return;
    }

    // Stop the Azure uploading state since batch is empty
    setIsPolling(false);

    // Clear the uploadBatchesQuery cache so this conditional works the next time we upload.
    queryClient.removeQueries({
      queryKey: uploadBatchesQueryKey,
    });

    // Invalidate and refetch the production files query to replace optimistic data
    queryClient.invalidateQueries({
      queryKey: purgatorySummaryQueryOptions.queryKey,
    });
  }, [
    uploadBatchesQuery.data,
    isPolling,
    uploadBatchesQueryKey,
    queryClient,
    purgatorySummaryQueryOptions.queryKey,
  ]);

  // After the batch is empty and polling is stopped, get the final batch to report errors
  const getBatchByIdQuery = useQuery({
    queryKey: ["caseFileApi.caseFileGetCaseFileUploadBatch", { batchId }],
    queryFn: () => {
      if (!urlParams.id) throw new Error("Case ID is required");
      if (!batchId) throw new Error("Batch ID is required");
      return caseFileApi.caseFileGetCaseFileUploadBatch({
        caseId: urlParams.id,
        uploadBatchId: batchId,
      });
    },
    enabled: !isPolling && !!batchId,
  });

  // Report errors from the final batch
  useEffect(() => {
    if (!getBatchByIdQuery.data?.errorResults || !batchId || isPolling) return;

    batchErrorToasts(getBatchByIdQuery.data.errorResults);

    // Clear the batch id from state so we're ready for the next upload
    setBatchId(null);
  }, [getBatchByIdQuery.data, batchId, isPolling, batchErrorToasts]);

  // Handle the upload more button click
  const handleUploadMoreChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    e.preventDefault();
    const files = e.target.files;
    const sanitizedFiles = sanitizeFiles(files ? Array.from(files) : [], true);

    // check if any file is pdf
    if (sanitizedFiles?.length) {
      uploadToAzureMutation.mutate(sanitizedFiles);
    } else {
      toast.error("Please upload a PDF file");
    }
  };

  const reviewMoreFiles = () => {
    /* reset file explorer to have no folder nav */
    history.replace({
      pathname: `/cases/${urlParams.id}`,
      search: `?review=true&tab=production&sub_tab=${searchParams.get("sub_tab")}`,
    });
  };

  // Check if the production files have been uploaded.
  const hasUploadedFiles = (purgatorySummaryQuery.data?.fileCount || 0) > 0;

  // Check if the upload is pending or if we're polling for the batch to be empty
  const isUploading = uploadToAzureMutation.isPending || isPolling;

  if (purgatorySummaryQuery.isLoading) return null;

  return (
    <>
      <input
        type="file"
        ref={uploadMoreInputRef}
        className="d-none"
        onChange={handleUploadMoreChange}
        accept={ACCEPTABLE_FILE_TYPES_WITH_IMAGES.join(",")}
        multiple
      />
      {hasUploadedFiles ? (
        <div
          data-testid="more-files-complete"
          className={`d-flex align-items-center ${styles.moreFilesComplete}`}
        >
          <CheckCircleFill size={16} className="me-2 text-success" />
          <div className={`me-2 ${styles.totalFilesText}`}>
            {purgatorySummaryQuery.data?.fileCount}
            &nbsp;
            {pluralize(purgatorySummaryQuery.data?.fileCount || 0, "document")}
            &nbsp;ready for processing
          </div>
          <Button
            data-testid="upload-more-button"
            size="sm"
            variant="link"
            disabled={isUploading}
            onClick={() => uploadMoreInputRef.current?.click()}
          >
            {isUploading ? "Uploading More..." : "Upload More"}
          </Button>
          <Button
            data-testid="review-more-files-button"
            size="sm"
            variant="primary"
            onClick={reviewMoreFiles}
            disabled={isUploading}
          >
            Review
          </Button>
        </div>
      ) : (
        <Button
          data-testid="upload-production-button"
          size="sm"
          className={styles.uploadButton}
          onClick={() => uploadMoreInputRef.current?.click()}
          disabled={isUploading}
        >
          {isUploading && <Spinner size="sm" />}
          {isUploading ? "Uploading More..." : "Upload More"}
        </Button>
      )}
    </>
  );
};

export default UploadMore;
